/**
 * Main Video Player orchestrator composable
 * Combines navigation, HLS, and native video element management.
 * No Pinia - all state lives in internal refs.
 */

import { ref, computed, watch, onUnmounted, shallowRef, triggerRef, nextTick, type Ref, type ComputedRef } from 'vue'
import type {
  VideoPlayerConfig,
  VideoPlayerState,
  VideoPlayerCallbacks,
  VideoPlayerOptions,
  QualityLevel,
  AudioTrack,
  SubtitleTrack,
} from '../types'
import { PanelType, PLAYBACK_SPEEDS, type PlaybackSpeed } from '../types'
import type { VideoAssetListItem, VideoPlaybackInfo, VideoChapter } from '../../types/asset'
import type { PlayerError, TimeRange } from '../../types/base'
import type { PlayState, PlaybackInfoState, CollectionItem } from '../../types/player'
import { useVideoNavigation, type UseVideoNavigationReturn } from './useVideoNavigation'
import { useHLS, type UseHLSReturn } from './useHLS'
import { usePlayerPreferences } from '../../common/composables/usePlayerPreferences'
import { usePlayhead } from '../../common/composables/usePlayhead'
import { detectSourceKind, type MediaSourceKind } from '../utils/source'
import { AssetType, createUUID } from '../../types/base'
import { playbackDownloadUrl } from '@/api/visitor/playback'

export interface UseVideoPlayerOptions extends VideoPlayerOptions {
  callbacks?: VideoPlayerCallbacks
  /**
   * Whether an entry that ends holds the playlist before the next one: `true` while the entry has
   * cards to show at its end, which the viewer is given time to read and answer.
   */
  holdAtEnd?: () => boolean
  /**
   * Whether the viewer may move the playhead — the publication's `seeking`, `true` when absent.
   * Read on every move, so a page that turns it on later is followed.
   */
  canSeek?: () => boolean
}

export interface UseVideoPlayerReturn {
  // Video element ref
  videoRef: Ref<HTMLVideoElement | null>
  containerRef: Ref<HTMLElement | null>

  // Items
  items: Ref<CollectionItem<VideoAssetListItem>[]>
  totalItems: ComputedRef<number>

  // Current item
  currentIndex: Ref<number>
  currentItem: ComputedRef<CollectionItem<VideoAssetListItem> | undefined>
  currentAsset: ComputedRef<VideoAssetListItem | undefined>
  currentPlaybackInfo: ComputedRef<VideoPlaybackInfo | undefined>
  currentHlsUrl: ComputedRef<string | undefined>
  hasDownloads: ComputedRef<boolean>
  sourceDownloadUrl: ComputedRef<string | undefined>

  // Playback state
  playState: Ref<PlayState>
  isPlaying: ComputedRef<boolean>
  isPaused: ComputedRef<boolean>
  isBuffering: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  isEnded: ComputedRef<boolean>
  hasStarted: Ref<boolean>
  showHomePage: Ref<boolean>

  // Time
  currentTime: Ref<number>
  duration: Ref<number>
  buffered: Ref<TimeRange[]>
  progress: ComputedRef<number>

  // Non-destructive trim window (see currentPlaybackInfo.trim)
  hasTrim: ComputedRef<boolean>
  trimStart: ComputedRef<number>
  trimEnd: ComputedRef<number>
  displayCurrentTime: ComputedRef<number>
  displayDuration: ComputedRef<number>

  // Volume
  volume: Ref<number>
  isMuted: Ref<boolean>

  // Playback speed
  playbackSpeed: Ref<number>
  availableSpeeds: readonly PlaybackSpeed[]

  // Quality (from HLS)
  availableQualities: Ref<QualityLevel[]>
  currentQualityIndex: Ref<number>
  isAutoQuality: Ref<boolean>
  setQualityLevel: (index: number) => void

  // Eco mode
  isEcoMode: Ref<boolean>
  toggleEcoMode: () => void
  /** Some quality is above 720p, so eco mode has something to hold back; its controls hide otherwise. */
  hasEcoHeadroom: Ref<boolean>

  // Audio tracks (from HLS)
  availableAudioTracks: Ref<AudioTrack[]>
  currentAudioTrackIndex: Ref<number>
  setAudioTrack: (index: number) => void
  hearingImpairedTrack: ComputedRef<AudioTrack | undefined>
  toggleHearingImpairedTrack: () => void

  // Subtitles (from HLS)
  subtitlesEnabled: Ref<boolean>
  availableSubtitleTracks: Ref<SubtitleTrack[]>
  currentSubtitleTrackIndex: Ref<number>
  setSubtitleTrack: (index: number) => void
  toggleSubtitles: () => void
  enableSubtitles: (trackIndex?: number) => void
  disableSubtitles: () => void

  // Subtitle display size (viewer preference, persisted)
  subtitleScale: Ref<number>
  setSubtitleScale: (scale: number) => void

  // Chapters
  chapters: ComputedRef<VideoChapter[]>
  currentChapterIndex: ComputedRef<number>

  // Navigation
  /** Whether the playlist waits on an entry that ended, for its end cards. */
  isHoldingAdvance: Ref<boolean>
  /** Move on from that wait to the next entry. */
  resumeAdvance: () => void
  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  isLoop: Ref<boolean>
  goToIndex: (index: number) => void
  next: () => void
  prev: () => void
  first: () => void
  last: () => void
  toggleLoop: () => void

  // Fullscreen & PiP
  isFullscreen: Ref<boolean>
  isPip: Ref<boolean>
  toggleFullscreen: () => Promise<void>
  togglePip: () => Promise<void>

  // Panel
  activePanel: Ref<PanelType | null>
  panelHighlight: Ref<string | null>
  isPanelOpen: ComputedRef<boolean>
  openPanel: (panel: PanelType, options?: { highlight?: string }) => void
  closePanel: () => void
  togglePanel: (panel: PanelType) => void

  // Control bar visibility
  showControlBar: Ref<boolean>

  // Playback controls
  play: () => Promise<void>
  pause: () => void
  togglePlay: () => Promise<void>
  /**
   * Whether the viewer may move the playhead (`seeking`). While it is `false` the bar is drawn
   * still, the keyboard's and the chapters' moves are refused, and a move the browser's own
   * controls make is put back (`common/composables/usePlayhead.ts`).
   */
  canSeek: ComputedRef<boolean>
  /** **The viewer's move** of the playhead — the bar, the keyboard, a chapter: refused while `canSeek` is `false`. */
  seek: (time: number) => void
  seekPercent: (percent: number) => void
  /**
   * **A move the viewer did not make**, which `seeking` does not govern: the command of the page
   * that framed the player (`docs/plans/embed-sdk.md` ES6) and the player's own resume point,
   * which lands where this viewer stopped.
   */
  seekTo: (time: number) => void
  setVolume: (volume: number) => void
  setMuted: (muted: boolean) => void
  toggleMute: () => void
  setPlaybackSpeed: (speed: number) => void
  startPlayback: () => Promise<void>

  // Playback info management
  getPlaybackInfo: (uid: string) => VideoPlaybackInfo | undefined
  setPlaybackInfo: (uid: string, info: VideoPlaybackInfo) => void
  requestPlaybackInfo: (uid: string) => void

  // HLS reference
  hlsManager: UseHLSReturn

  // Error
  error: Ref<PlayerError | undefined>
  clearError: () => void

  // State
  state: ComputedRef<VideoPlayerState>

  // Lifecycle
  destroy: () => void
  initAutoPlay: () => void
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

export function useVideoPlayer(options: UseVideoPlayerOptions): UseVideoPlayerReturn {
  const { config, initialPlaybackInfo, poster, callbacks, holdAtEnd } = options

  // Element refs
  const videoRef = shallowRef<HTMLVideoElement | null>(null)
  const containerRef = shallowRef<HTMLElement | null>(null)

  // Every move of the playhead the player makes, and the net beneath the controls for the ones it
  // did not: put back while the viewer may not seek.
  const playhead = usePlayhead({
    element: () => videoRef.value,
    canSeek: options.canSeek,
    span: () => ({
      start: trimStart.value,
      end: trimEnd.value > 0 ? trimEnd.value : (videoRef.value?.duration ?? 0),
    }),
  })

  // Direct-source mode: playing a raw URL instead of an Asset/PlaybackInfo.
  // When `config.src` is set we synthesize a single-item list so the rest of
  // the player (navigation, homepage overlay, UI) keeps working with minimal
  // branching.
  const hasDirectSource = !!config.src

  const syntheticItem: CollectionItem<VideoAssetListItem> | null = hasDirectSource
    ? {
        asset: {
          uid: createUUID('direct'),
          name: config.title || '',
          type: AssetType.VIDEO,
          duration: 0,
          dimensions: { width: 0, height: 0 },
          hasAudio: true,
        },
        position: 0,
      }
    : null

  // Items state
  const items = ref<CollectionItem<VideoAssetListItem>[]>(
    config.items && config.items.length > 0
      ? config.items
      : syntheticItem
        ? [syntheticItem]
        : [],
  )

  // Playback state
  const playState = ref<PlayState>('idle')
  const currentTime = ref(0)
  const duration = ref(0)
  const buffered = ref<TimeRange[]>([])
  const hasStarted = ref(false)
  const showHomePage = ref(true)

  // Start-at (consumed once on first metadata load)
  const pendingStartAt = ref(config.startAt ?? 0)

  // Volume
  const volume = ref(1)
  const isMuted = ref(config.muted ?? false)

  // Playback speed
  const playbackSpeed = ref(config.playbackSpeed ?? 1)

  // Subtitles
  const subtitlesEnabled = ref(config.subtitlesEnabled ?? false)

  // Viewer preferences (subtitle size, …) — persisted globally in localStorage
  const { subtitleScale, setSubtitleScale } = usePlayerPreferences()

  // Fullscreen & PiP
  const isFullscreen = ref(false)
  const isPip = ref(false)

  // Panel state
  const activePanel = ref<PanelType | null>(null)
  const panelHighlight = ref<string | null>(null)
  const isPanelOpen = computed(() => activePanel.value !== null)

  // Control bar visibility — written by VideoPlayer from useControlBarVisibility
  const showControlBar = ref(true)

  // Seeking state (suppresses pause/waiting flicker during scrub)
  const isSeeking = ref(false)

  // End-of-playback latch: prevents handleEnded from firing twice for the
  // same playback when both the native 'ended' event and the handleTimeUpdate
  // fallback trigger (would otherwise skip one item in the playlist).
  let endedFired = false

  // Error state
  const error = ref<PlayerError | undefined>()

  // Playback info cache
  const playbackInfoCache = shallowRef<Map<string, VideoPlaybackInfo>>(
    initialPlaybackInfo
      ? new Map([[initialPlaybackInfo.uid, initialPlaybackInfo]])
      : new Map(),
  )

  // Extract assets for navigation
  const assetItems = computed(() => items.value.map((item) => item.asset))

  // Navigation composable
  const navigation: UseVideoNavigationReturn = useVideoNavigation({
    items: assetItems,
    initialIndex: config.currentIndex || 0,
    loop: config.loop ?? false,
    autoAdvance: config.autoPlayNext ?? true,
    onIndexChange: (index) => {
      const item = items.value[index]
      if (item) {
        callbacks?.onItemChange?.(index, item)
      }
      loadCurrentPlaybackInfo()
    },
    onTrackEnd: () => {
      callbacks?.onEnded?.()
    },
    onCollectionEnd: () => {
      callbacks?.onCollectionEnd?.()
      playState.value = 'ended'
    },
    holdAtEnd,
    // Held, the entry has ended as the last one does: that is what fires its end cards.
    onHold: () => {
      playState.value = 'ended'
    },
  })

  // HLS composable
  const hlsManager: UseHLSReturn = useHLS({
    videoRef,
    debug: false,
    deferredLoad: !config.autoPlay,
    ecoModeDefault: config.ecoModeDefault ?? false,
    onError: (errorMsg) => {
      setError({
        code: 'PLAYBACK_ERROR',
        message: errorMsg,
        recoverable: true,
      })
    },
    onManifestParsed: () => {
      // HLS ready
    },
    onBufferingChange: (buffering) => {
      if (buffering && playState.value === 'playing') {
        playState.value = 'buffering'
      }
    },
    onBufferFlushed: refreshBuffered,
  })

  // Computed
  const totalItems = computed(() => items.value.length)
  const currentItem = computed(() => items.value[navigation.currentIndex.value])
  const currentAsset = computed(() => currentItem.value?.asset)

  const currentPlaybackInfo = computed(() => {
    const asset = currentAsset.value
    if (!asset) return undefined
    return playbackInfoCache.value.get(asset.uid)
  })

  const currentHlsUrl = computed(() => {
    return currentPlaybackInfo.value?.hlsUrl?.url
  })

  // Unified media source: either the direct URL from `config.src`, the HLS
  // manifest URL from the current PlaybackInfo, or — for legacy assets without
  // HLS renditions — the progressive source URL. The `kind` drives whether we
  // use hls.js or set `video.src` natively.
  const currentMediaSource = computed<{ kind: MediaSourceKind; url: string } | undefined>(() => {
    if (config.src) {
      return { kind: detectSourceKind(config.src), url: config.src }
    }
    const hlsUrl = currentHlsUrl.value
    if (hlsUrl) return { kind: 'hls', url: hlsUrl }
    const sourceUrl = currentPlaybackInfo.value?.sourceUrl?.url
    if (sourceUrl) return { kind: detectSourceKind(sourceUrl), url: sourceUrl }
    return undefined
  })

  const hasDownloads = computed(() => {
    const info = currentPlaybackInfo.value
    const hasLinkedFiles = !!(info?.linkedFiles && info.linkedFiles.length > 0)
    const hasSourceDownload = !!config.allowSourceDownload
    return hasLinkedFiles || hasSourceDownload
  })

  // A minted link expires, so it is asked for when the panel that shows it opens, item by item.
  const mintedSourceDownload = ref<{ uid: string; url: string } | null>(null)
  const mintSourceDownload = config.sourceDownload
  if (mintSourceDownload && config.allowSourceDownload) {
    watch(
      () => (activePanel.value === PanelType.INFO ? currentAsset.value?.uid : undefined),
      async (uid) => {
        if (!uid) return
        const url = await mintSourceDownload(uid)
        if (url && currentAsset.value?.uid === uid) mintedSourceDownload.value = { uid, url }
      },
    )
  }

  const sourceDownloadUrl = computed(() => {
    if (!config.allowSourceDownload) return undefined
    const asset = currentAsset.value
    if (!asset) return undefined
    if (mintSourceDownload) {
      const minted = mintedSourceDownload.value
      return minted?.uid === asset.uid ? minted.url : undefined
    }
    if (!config.playbackToken) return undefined
    return playbackDownloadUrl(asset.uid, config.playbackToken)
  })

  const isPlaying = computed(() => playState.value === 'playing')
  const isPaused = computed(() => playState.value === 'paused')
  const isBuffering = computed(() => playState.value === 'buffering' || hlsManager.isBuffering.value)
  const isLoading = computed(() => playState.value === 'loading')
  const isEnded = computed(() => playState.value === 'ended')

  const progress = computed(() => {
    if (duration.value <= 0) return 0
    return (currentTime.value / duration.value) * 100
  })

  // Non-destructive playback trim. When the asset carries a trim window we clamp
  // playback to [trimStart, trimEnd]; currentTime stays on the absolute media
  // timeline (so subtitles/chapters/sprites keep aligning) and only the playback
  // bounds and the displayed time/duration are windowed.
  const hasTrim = computed(() => {
    const t = currentPlaybackInfo.value?.trim
    return !!t && t.end > t.start
  })
  const trimStart = computed(() => Math.max(0, currentPlaybackInfo.value?.trim?.start ?? 0))
  const trimEnd = computed(() => {
    const end = currentPlaybackInfo.value?.trim?.end
    if (hasTrim.value && end != null) {
      return duration.value > 0 ? Math.min(end, duration.value) : end
    }
    return duration.value
  })
  // trimStart/trimEnd already collapse to 0/duration when there's no trim, so the
  // windowed display values need no separate no-trim branch.
  const effectiveDuration = computed(() => Math.max(0, trimEnd.value - trimStart.value))
  const displayCurrentTime = computed(() =>
    clamp(currentTime.value - trimStart.value, 0, effectiveDuration.value),
  )
  const displayDuration = effectiveDuration

  // Chapters
  const chapters = computed<VideoChapter[]>(() => {
    return currentPlaybackInfo.value?.chapters || []
  })

  const currentChapterIndex = computed(() => {
    const chaps = chapters.value
    if (chaps.length === 0) return -1
    const time = currentTime.value
    for (let i = chaps.length - 1; i >= 0; i--) {
      if (time >= chaps[i].startTime) return i
    }
    return -1
  })

  // Playback info state
  const playbackInfoState = computed<PlaybackInfoState>(() => ({
    isLoading: playState.value === 'loading' && !currentPlaybackInfo.value,
    isLoaded: !!currentPlaybackInfo.value,
  }))

  // Full state
  const state = computed<VideoPlayerState>(() => ({
    playState: playState.value,
    currentTime: currentTime.value,
    duration: duration.value,
    volume: volume.value,
    isMuted: isMuted.value,
    playbackSpeed: playbackSpeed.value,

    currentQualityIndex: hlsManager.currentQualityIndex.value,
    isAutoQuality: hlsManager.isAutoQuality.value,
    availableQualities: hlsManager.availableQualities.value,

    currentAudioTrackIndex: hlsManager.currentAudioTrackIndex.value,
    availableAudioTracks: hlsManager.availableAudioTracks.value,

    subtitlesEnabled: subtitlesEnabled.value,
    currentSubtitleTrackIndex: hlsManager.currentSubtitleTrackIndex.value,
    availableSubtitleTracks: hlsManager.availableSubtitleTracks.value,

    isFullscreen: isFullscreen.value,
    isPip: isPip.value,
    currentChapterIndex: currentChapterIndex.value,

    error: error.value,
    playbackInfo: playbackInfoState.value,
  }))

  // ==============================
  // Video event handlers
  // ==============================

  function bindVideoEvents(video: HTMLVideoElement): void {
    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('playing', handlePlaying)
    video.addEventListener('waiting', handleWaiting)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('durationchange', handleDurationChange)
    video.addEventListener('progress', handleProgress)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)
    video.addEventListener('seeking', handleSeeking)
    video.addEventListener('seeked', handleSeeked)
    video.addEventListener('volumechange', handleVolumeChange)
    video.addEventListener('ratechange', handleRateChange)
    video.addEventListener('enterpictureinpicture', handleEnterPip)
    video.addEventListener('leavepictureinpicture', handleLeavePip)
  }

  function unbindVideoEvents(video: HTMLVideoElement): void {
    video.removeEventListener('loadstart', handleLoadStart)
    video.removeEventListener('loadedmetadata', handleLoadedMetadata)
    video.removeEventListener('canplay', handleCanPlay)
    video.removeEventListener('play', handlePlay)
    video.removeEventListener('pause', handlePause)
    video.removeEventListener('playing', handlePlaying)
    video.removeEventListener('waiting', handleWaiting)
    video.removeEventListener('timeupdate', handleTimeUpdate)
    video.removeEventListener('durationchange', handleDurationChange)
    video.removeEventListener('progress', handleProgress)
    video.removeEventListener('ended', handleEnded)
    video.removeEventListener('error', handleError)
    video.removeEventListener('seeking', handleSeeking)
    video.removeEventListener('seeked', handleSeeked)
    video.removeEventListener('volumechange', handleVolumeChange)
    video.removeEventListener('ratechange', handleRateChange)
    video.removeEventListener('enterpictureinpicture', handleEnterPip)
    video.removeEventListener('leavepictureinpicture', handleLeavePip)
  }

  function handleLoadStart(): void {
    playState.value = 'loading'
    playhead.onLoadStart()
    // Whatever was buffered belonged to the media being replaced. Left alone it stays on the bar
    // until the first `progress` of the new one.
    buffered.value = []
  }

  function handleLoadedMetadata(): void {
    const video = videoRef.value
    if (video) {
      duration.value = video.duration
      callbacks?.onDurationChange?.(video.duration)

      if (Number.isFinite(video.duration)) {
        const lower = trimStart.value
        const upper = trimEnd.value > 0 ? trimEnd.value : video.duration
        // Seek to startAt/resume position on first load, clamped to the trim window.
        if (pendingStartAt.value > 0) {
          const target = clamp(pendingStartAt.value, lower, upper)
          playhead.place(target)
          currentTime.value = target
          pendingStartAt.value = 0
        } else if (lower > 0) {
          // Honor the trim start as the initial playhead.
          playhead.place(lower)
          currentTime.value = lower
        }
      }
    }
  }

  function handleCanPlay(): void {
    callbacks?.onCanPlay?.()
    if (playState.value === 'loading' || playState.value === 'buffering') {
      playState.value = 'paused'
    }
  }

  function handlePlay(): void {
    endedFired = false
    callbacks?.onPlay?.()
  }

  function handlePause(): void {
    if (isSeeking.value) return
    playState.value = 'paused'
    callbacks?.onPause?.()
  }

  function handlePlaying(): void {
    playState.value = 'playing'
  }

  function handleSeeking(): void {
    isSeeking.value = true
    playhead.onSeeking()
  }

  function handleSeeked(): void {
    isSeeking.value = false
    playhead.onSeeked()
    refreshBuffered()
    const video = videoRef.value
    if (video && !video.paused) {
      playState.value = 'playing'
    }
  }

  function handleWaiting(): void {
    if (isSeeking.value) return
    playState.value = 'buffering'
    callbacks?.onWaiting?.()
  }

  function handleTimeUpdate(): void {
    const video = videoRef.value
    if (video) {
      // Keep the playhead inside the trim window when one is set (e.g. a native
      // scrub that lands before the window start).
      if (hasTrim.value && video.currentTime < trimStart.value - 0.25) {
        playhead.place(trimStart.value)
      }

      currentTime.value = video.currentTime
      playhead.onTimeUpdate()
      callbacks?.onTimeUpdate?.(video.currentTime)

      // End detection at the effective end: trimEnd when trimmed (stop actively,
      // even while playing), otherwise the media duration with the original
      // HLS.js paused-near-duration fallback.
      const effectiveEnd = trimEnd.value
      if (
        playState.value === 'playing' &&
        effectiveEnd > 0 &&
        video.currentTime >= effectiveEnd - (hasTrim.value ? 0.05 : 0.15) &&
        (hasTrim.value || video.paused)
      ) {
        if (hasTrim.value && !video.paused) video.pause()
        handleEnded()
      }

      // Chapter change detection
      const prevChapter = currentChapterIndex.value
      const newChapter = computeCurrentChapter(video.currentTime)
      if (newChapter !== prevChapter && newChapter >= 0) {
        const chapter = chapters.value[newChapter]
        if (chapter) {
          callbacks?.onChapterChange?.(newChapter, chapter.title)
        }
      }
    }
  }

  function computeCurrentChapter(time: number): number {
    const chaps = chapters.value
    if (chaps.length === 0) return -1
    for (let i = chaps.length - 1; i >= 0; i--) {
      if (time >= chaps[i].startTime) return i
    }
    return -1
  }

  function handleDurationChange(): void {
    const video = videoRef.value
    if (video) {
      duration.value = video.duration
      callbacks?.onDurationChange?.(video.duration)
    }
  }

  function handleProgress(): void {
    const video = videoRef.value
    if (video) {
      buffered.value = calculateBufferedRanges(video)
      callbacks?.onProgress?.(buffered.value)
    }
  }

  /**
   * Re-read `video.buffered` outside the `progress` event.
   *
   * **`progress` fires while media data is *downloaded*, so it only ever reports the buffer
   * growing.** Nothing at all is emitted when a range goes away — hls.js drops one on a seek
   * outside the buffered window, and a browser evicts under memory pressure — so without this
   * the bar goes on painting a range that no longer exists until the next append happens to
   * refresh it. Called from `seeked` and from hls.js's own `BUFFER_FLUSHED`.
   */
  function refreshBuffered(): void {
    const video = videoRef.value
    if (video) {
      buffered.value = calculateBufferedRanges(video)
    }
  }

  function handleEnded(): void {
    if (endedFired) return
    endedFired = true
    navigation.handleTrackEnd()
  }

  function handleError(): void {
    const video = videoRef.value
    if (!video) return

    const mediaError = video.error
    let errorCode: PlayerError['code'] = 'PLAYBACK_ERROR'
    let errorMessage = 'Video playback error'

    if (mediaError) {
      switch (mediaError.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMessage = 'Playback aborted'
          break
        case MediaError.MEDIA_ERR_NETWORK:
          errorCode = 'NETWORK_ERROR'
          errorMessage = 'Network error while loading video'
          break
        case MediaError.MEDIA_ERR_DECODE:
          errorCode = 'DECODE_ERROR'
          errorMessage = 'Video decoding error'
          break
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorCode = 'FORMAT_NOT_SUPPORTED'
          errorMessage = 'Video format not supported'
          break
      }
    }

    error.value = { code: errorCode, message: errorMessage, recoverable: true }
    playState.value = 'error'
    callbacks?.onError?.(error.value)
  }

  function handleVolumeChange(): void {
    const video = videoRef.value
    if (video) {
      volume.value = video.volume
      isMuted.value = video.muted
      callbacks?.onVolumeChange?.(video.volume, video.muted)
    }
  }

  function handleRateChange(): void {
    const video = videoRef.value
    if (video) {
      playbackSpeed.value = video.playbackRate
      callbacks?.onPlaybackSpeedChange?.(video.playbackRate)
    }
  }

  function handleEnterPip(): void {
    isPip.value = true
    callbacks?.onPipChange?.(true)
  }

  function handleLeavePip(): void {
    isPip.value = false
    callbacks?.onPipChange?.(false)
  }

  function calculateBufferedRanges(video: HTMLVideoElement): TimeRange[] {
    const ranges: TimeRange[] = []
    for (let i = 0; i < video.buffered.length; i++) {
      ranges.push({ start: video.buffered.start(i), end: video.buffered.end(i) })
    }
    // Merge ranges that are close together (< 1s gap) to avoid visual fragmentation during quality switches
    if (ranges.length <= 1) return ranges
    ranges.sort((a, b) => a.start - b.start)
    const merged: TimeRange[] = [ranges[0]]
    for (let i = 1; i < ranges.length; i++) {
      const prev = merged[merged.length - 1]
      if (ranges[i].start - prev.end < 1) {
        prev.end = Math.max(prev.end, ranges[i].end)
      } else {
        merged.push(ranges[i])
      }
    }
    return merged
  }

  // ==============================
  // Media source loading (HLS vs native)
  // ==============================

  /** Load the current media source into the <video> element. */
  function loadMediaSource(source: { kind: MediaSourceKind; url: string }): void {
    if (source.kind === 'hls') {
      hlsManager.initHLS(source.url)
      return
    }
    // Native: attach URL directly, tear down any existing HLS instance.
    hlsManager.destroyHLS()
    const video = videoRef.value
    if (video && video.src !== source.url) {
      video.src = source.url
      video.load()
    }
  }

  // ==============================
  // Playback info management
  // ==============================

  async function loadCurrentPlaybackInfo(): Promise<void> {
    // Direct-source mode bypasses the PlaybackInfo system entirely.
    if (hasDirectSource) return

    const asset = currentAsset.value
    if (!asset) return

    if (playbackInfoCache.value.has(asset.uid)) return

    playState.value = 'loading'
    if (callbacks?.onPlaybackInfoNeeded) {
      try {
        const info = await callbacks.onPlaybackInfoNeeded(asset.uid as any)
        if (info && info.type === 'video') {
          setPlaybackInfo(asset.uid, info as VideoPlaybackInfo)
        }
      } catch (err) {
        setError({
          code: 'PLAYBACK_INFO_FETCH_FAILED',
          message: err instanceof Error ? err.message : 'Failed to load video info',
          recoverable: true,
        })
      }
    }
  }

  function getPlaybackInfo(uid: string): VideoPlaybackInfo | undefined {
    return playbackInfoCache.value.get(uid)
  }

  function setPlaybackInfo(uid: string, info: VideoPlaybackInfo): void {
    playbackInfoCache.value.set(uid, info)
    triggerRef(playbackInfoCache)

    if (currentAsset.value?.uid === uid) {
      if (playState.value === 'loading') {
        playState.value = 'idle'
      }
    }
  }

  function requestPlaybackInfo(uid: string): void {
    if (!playbackInfoCache.value.has(uid) && callbacks?.onPlaybackInfoNeeded) {
      callbacks
        .onPlaybackInfoNeeded(uid as any)
        .then((info) => {
          if (info && info.type === 'video') {
            setPlaybackInfo(uid, info as VideoPlaybackInfo)
          }
        })
        .catch(() => {
          // Silently ignore prefetch errors
        })
    }
  }

  // ==============================
  // Playback controls
  // ==============================

  async function play(): Promise<void> {
    const video = videoRef.value
    if (!video) return

    if (!currentMediaSource.value) {
      await loadCurrentPlaybackInfo()
    }

    try {
      await video.play()
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError({ code: 'PLAYBACK_ERROR', message: err.message, recoverable: true })
      }
    }
  }

  async function playWithAutoplayCheck(): Promise<boolean> {
    const video = videoRef.value
    if (!video) return false

    if (!currentMediaSource.value) {
      await loadCurrentPlaybackInfo()
    }

    try {
      await video.play()
      return true
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        // Browsers refuse to start a sound without a gesture and allow a silent one: an autoplay
        // refused with its sound is retried muted, and the muted indicator offers the sound back.
        if (video.muted) return false
        try {
          video.muted = true
          await video.play()
          return true
        } catch {
          return false
        }
      }
      if (err instanceof Error && err.name !== 'AbortError') {
        setError({ code: 'PLAYBACK_ERROR', message: err.message, recoverable: true })
      }
      return false
    }
  }

  function pause(): void {
    videoRef.value?.pause()
  }

  async function togglePlay(): Promise<void> {
    if (isPlaying.value) {
      pause()
    } else {
      await play()
    }
  }

  async function startPlayback(): Promise<void> {
    showHomePage.value = false
    hasStarted.value = true

    const source = currentMediaSource.value
    if (source) {
      if (source.kind === 'hls') {
        if (!hlsManager.hls.value) {
          hlsManager.initHLS(source.url)
        }
        // Pass startAt so HLS begins loading from the right position
        const startPos = pendingStartAt.value > 0 ? pendingStartAt.value : undefined
        if (startPos !== undefined) playhead.permit(startPos)
        hlsManager.startLoad(startPos)
      } else {
        loadMediaSource(source)
      }
    }

    await play()
  }

  function seek(time: number): void {
    if (!playhead.canSeek.value) return
    seekTo(time)
  }

  function seekTo(time: number): void {
    const video = videoRef.value
    if (video && Number.isFinite(video.duration)) {
      const lower = trimStart.value
      const upper = trimEnd.value > 0 ? trimEnd.value : video.duration
      const targetTime = clamp(time, lower, upper)
      const fromTime = video.currentTime
      callbacks?.onSeeking?.(targetTime)
      playhead.place(targetTime)
      callbacks?.onSeeked?.(fromTime, targetTime)
    }
  }

  function seekPercent(percent: number): void {
    const clampedPercent = clamp(percent, 0, 100)
    const targetTime = (clampedPercent / 100) * duration.value
    seek(targetTime)
  }

  function setVolume(newVolume: number): void {
    const video = videoRef.value
    if (video) {
      video.volume = clamp(newVolume, 0, 1)
    }
  }

  function setMuted(muted: boolean): void {
    const video = videoRef.value
    if (video) {
      video.muted = muted
    }
  }

  function toggleMute(): void {
    setMuted(!isMuted.value)
  }

  function setPlaybackSpeed(speed: number): void {
    const video = videoRef.value
    if (video) {
      video.playbackRate = speed
    }
  }

  // ==============================
  // Subtitles
  // ==============================

  // Whether the captions on screen were turned on by the publication's auto-start rather than by
  // the viewer. Auto-started captions are decided again on each playlist item, from that item's own
  // default track; captions the viewer asked for stay on from one item to the next.
  let subtitlesAutoStarted = false

  function toggleSubtitles(): void {
    if (subtitlesEnabled.value) {
      disableSubtitles()
    } else {
      enableSubtitles()
    }
  }

  function enableSubtitles(trackIndex?: number): void {
    subtitlesAutoStarted = false
    showSubtitles(trackIndex)
  }

  // Captions on, on the given track or — none given and none showing — the viewer's language, the
  // default track, else the first: somebody asked for captions, so a track shows.
  function showSubtitles(trackIndex?: number): void {
    subtitlesEnabled.value = true
    const tracks = hlsManager.availableSubtitleTracks.value
    if (trackIndex !== undefined) {
      hlsManager.setSubtitleTrack(trackIndex)
    } else if (hlsManager.currentSubtitleTrackIndex.value === -1 && tracks.length > 0) {
      // Try preferred language first, then default track, then first track
      let match: typeof tracks[0] | undefined
      if (config.preferredSubtitleLanguage) {
        const lang = config.preferredSubtitleLanguage
        match = tracks.find(t => t.language.startsWith(lang))
        if (!match && lang !== 'fr') {
          match = tracks.find(t => t.language.startsWith('en'))
        }
      }
      if (!match) {
        match = tracks.find(t => t.isDefault)
      }
      hlsManager.setSubtitleTrack(match?.index ?? tracks[0].index)
    }
  }

  function disableSubtitles(): void {
    subtitlesAutoStarted = false
    subtitlesEnabled.value = false
    hlsManager.setSubtitleTrack(-1)
  }

  // ==============================
  // Fullscreen & PiP
  // ==============================

  async function toggleFullscreen(): Promise<void> {
    const container = containerRef.value
    if (!container) return

    // iPhone Safari takes no element fullscreen, only a <video>'s own: iOS's
    // player then covers ours, with its controls and its subtitle rendering.
    const video = videoRef.value as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
        isFullscreen.value = false
      } else if (document.fullscreenEnabled) {
        await container.requestFullscreen()
        isFullscreen.value = true
      } else if (video?.webkitEnterFullscreen) {
        video.addEventListener(
          'webkitendfullscreen',
          () => {
            isFullscreen.value = false
            callbacks?.onFullscreenChange?.(false)
          },
          { once: true },
        )
        video.webkitEnterFullscreen()
        isFullscreen.value = true
      } else {
        return
      }
      callbacks?.onFullscreenChange?.(isFullscreen.value)
    } catch {
      // Fullscreen not available
    }
  }

  async function togglePip(): Promise<void> {
    const video = videoRef.value
    if (!video || !document.pictureInPictureEnabled) return

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture()
      } else {
        await video.requestPictureInPicture()
      }
    } catch {
      // PiP not available
    }
  }

  // ==============================
  // Panel management
  // ==============================

  function openPanel(panel: PanelType, options?: { highlight?: string }): void {
    activePanel.value = panel
    panelHighlight.value = options?.highlight ?? null
  }

  function closePanel(): void {
    activePanel.value = null
    panelHighlight.value = null
  }

  function togglePanel(panel: PanelType): void {
    if (activePanel.value === panel) {
      closePanel()
    } else {
      openPanel(panel)
    }
  }

  // ==============================
  // Error management
  // ==============================

  function clearError(): void {
    error.value = undefined
    if (playState.value === 'error') {
      playState.value = 'idle'
    }
  }

  function setError(err: PlayerError): void {
    error.value = err
    playState.value = 'error'
    callbacks?.onError?.(err)
  }

  // ==============================
  // Lifecycle
  // ==============================

  function destroy(): void {
    const video = videoRef.value
    if (video) {
      video.pause()
      video.src = ''
      unbindVideoEvents(video)
    }
    hlsManager.destroyHLS()
    playbackInfoCache.value.clear()
    triggerRef(playbackInfoCache)
  }

  // ==============================
  // Watchers
  // ==============================

  // Watch for video element changes
  watch(videoRef, (newVideo, oldVideo) => {
    if (oldVideo) {
      unbindVideoEvents(oldVideo)
    }
    if (newVideo) {
      bindVideoEvents(newVideo)
      newVideo.volume = volume.value
      newVideo.muted = isMuted.value
      newVideo.playbackRate = playbackSpeed.value
    }
  })

  // Watch for media source changes — load it into the video element when
  // it appears (navigation to next item, PlaybackInfo resolved, etc.)
  watch(currentMediaSource, (next, prev) => {
    if (!next) return

    // Skip if this is the initial value — handled by initAutoPlay via onMounted
    if (!prev && config.autoPlay) return

    // If the URL hasn't changed (e.g. PlaybackInfo refreshed with same URL), skip.
    if (prev && prev.url === next.url && prev.kind === next.kind) return

    loadMediaSource(next)

    if (hasStarted.value || config.autoPlay) {
      showHomePage.value = false
      hasStarted.value = true
      if (next.kind === 'hls') {
        const startPos = pendingStartAt.value > 0 ? pendingStartAt.value : undefined
        hlsManager.startLoad(startPos)
      }
      nextTick(() => {
        play()
      })
    }
  })

  // Auto-play: load the media source and start playback once videoRef is available
  function initAutoPlay(): void {
    if (!config.autoPlay) return
    const source = currentMediaSource.value
    if (!source) return

    showHomePage.value = false
    hasStarted.value = true
    loadMediaSource(source)
    if (source.kind === 'hls') {
      const startPos = pendingStartAt.value > 0 ? pendingStartAt.value : undefined
      hlsManager.startLoad(startPos)
    }
    nextTick(async () => {
      const success = await playWithAutoplayCheck()
      if (!success) {
        showHomePage.value = true
        hasStarted.value = false
      }
    })
  }

  // Sync subtitlesEnabled with HLS track state
  watch(subtitlesEnabled, (enabled) => {
    if (!enabled) {
      hlsManager.setSubtitleTrack(-1)
    } else if (hlsManager.currentSubtitleTrackIndex.value === -1) {
      showSubtitles()
    }
  })

  // Auto-select preferred subtitle language when tracks become available
  // Fallback: FR browser → FR track; other browser → EN fallback
  watch(hlsManager.availableSubtitleTracks, (tracks) => {
    if (tracks.length === 0) return

    // If the viewer turned subtitles on (e.g. on the previous playlist video),
    // reactivate a track on the new video immediately.
    if (subtitlesEnabled.value && !subtitlesAutoStarted) {
      showSubtitles()
      return
    }

    let autoStarted = false
    if (config.preferredSubtitleLanguage) {
      // auto_detect mode: match browser language
      const lang = config.preferredSubtitleLanguage
      let match = tracks.find(t => t.language.startsWith(lang))
      if (!match && lang !== 'fr') {
        match = tracks.find(t => t.language.startsWith('en'))
      }
      if (match) {
        hlsManager.setSubtitleTrack(match.index)
        autoStarted = config.subtitlesAutoStart === true
      }
    } else if (config.subtitlesAutoStart) {
      // specific mode: the default track (DEFAULT=YES in the manifest), and none when the asset
      // has none — an owner who chose no track at start gets no captions at start.
      const defaultTrack = tracks.find((t) => t.isDefault)
      if (defaultTrack) {
        hlsManager.setSubtitleTrack(defaultTrack.index)
        autoStarted = true
      }
    }
    subtitlesAutoStarted = autoStarted
    subtitlesEnabled.value = autoStarted
  })

  // Sync subtitlesEnabled when HLS.js auto-selects a default track (DEFAULT=YES in manifest)
  watch(hlsManager.currentSubtitleTrackIndex, (index) => {
    if (index >= 0 && !subtitlesEnabled.value) {
      if (config.subtitlesAutoStart) {
        subtitlesAutoStarted = true
        subtitlesEnabled.value = true
      } else {
        // HLS.js auto-selected a default track but auto-start is off — disable it
        hlsManager.setSubtitleTrack(-1)
      }
    }
  })

  // Watch fullscreen changes from browser (e.g. Escape key)
  function handleFullscreenChange(): void {
    isFullscreen.value = !!document.fullscreenElement
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
  }

  // Watch for items changes
  watch(
    () => config.items,
    (newItems) => {
      if (newItems) {
        items.value = newItems
      }
    },
    { deep: true },
  )

  // Initial load
  if (items.value.length > 0) {
    loadCurrentPlaybackInfo()
  }

  // Cleanup on unmount
  onUnmounted(() => {
    destroy()
    if (typeof document !== 'undefined') {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  })

  return {
    videoRef,
    containerRef,

    items,
    totalItems,

    currentIndex: navigation.currentIndex,
    currentItem,
    currentAsset,
    currentPlaybackInfo,
    currentHlsUrl,
    hasDownloads,
    sourceDownloadUrl,

    playState,
    isPlaying,
    isPaused,
    isBuffering,
    isLoading,
    isEnded,
    hasStarted,
    showHomePage,

    currentTime,
    duration,
    buffered,
    progress,

    hasTrim,
    trimStart,
    trimEnd,
    displayCurrentTime,
    displayDuration,

    volume,
    isMuted,

    playbackSpeed,
    availableSpeeds: PLAYBACK_SPEEDS,

    availableQualities: hlsManager.availableQualities,
    currentQualityIndex: hlsManager.currentQualityIndex,
    isAutoQuality: hlsManager.isAutoQuality,
    setQualityLevel: hlsManager.setQualityLevel,

    isEcoMode: hlsManager.isEcoMode,
    toggleEcoMode: hlsManager.toggleEcoMode,
    hasEcoHeadroom: hlsManager.hasEcoHeadroom,

    availableAudioTracks: hlsManager.availableAudioTracks,
    currentAudioTrackIndex: hlsManager.currentAudioTrackIndex,
    setAudioTrack: hlsManager.setAudioTrack,
    hearingImpairedTrack: hlsManager.hearingImpairedTrack,
    toggleHearingImpairedTrack: hlsManager.toggleHearingImpairedTrack,

    subtitlesEnabled,
    availableSubtitleTracks: hlsManager.availableSubtitleTracks,
    currentSubtitleTrackIndex: hlsManager.currentSubtitleTrackIndex,
    setSubtitleTrack: hlsManager.setSubtitleTrack,
    toggleSubtitles,
    enableSubtitles,
    disableSubtitles,

    subtitleScale,
    setSubtitleScale,

    chapters,
    currentChapterIndex,

    isHoldingAdvance: navigation.isHolding,
    resumeAdvance: navigation.resumeAdvance,
    canGoNext: navigation.canGoNext,
    canGoPrev: navigation.canGoPrev,
    isLoop: navigation.isLoop,
    goToIndex: navigation.goToIndex,
    next: () => { navigation.next() },
    prev: navigation.prev,
    first: navigation.first,
    last: navigation.last,
    toggleLoop: navigation.toggleLoop,

    isFullscreen,
    isPip,
    toggleFullscreen,
    togglePip,

    activePanel,
    panelHighlight,
    isPanelOpen,
    openPanel,
    closePanel,
    togglePanel,

    showControlBar,

    play,
    pause,
    togglePlay,
    canSeek: playhead.canSeek,
    seek,
    seekPercent,
    seekTo,
    setVolume,
    setMuted,
    toggleMute,
    setPlaybackSpeed,
    startPlayback,

    getPlaybackInfo,
    setPlaybackInfo,
    requestPlaybackInfo,

    hlsManager,

    error,
    clearError,

    state,

    destroy,
    initAutoPlay,
  }
}
